
import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useAuthContext } from "../AuthContext";
import FormHelper from "../../helpers/FormHelper";
import PartnerPmsService from "../../services/PartnerPmsService";
import { IBlock, IExpectedOccupancy, IFloor, IReservation, IRoom, IRoomType } from "../../interfaces/IPartnerPms";
import { GovernanceStatusData, GovernanceStatusEnum, OccupancyStatusData, OccupancyStatusEnum, RoomStatusData, RoomStatusEnum } from "../../data/Enums";
import useState from "../../hooks/useState";


interface IPartnerPmsContext {
    selectedOccupancyStatus: OccupancyStatusEnum[];
    selectedGovernanceStatus: GovernanceStatusEnum[];
    selectedRoomStatus: RoomStatusEnum[];
    selectedBlock: IBlock[];
    selectedFloor: IFloor[];
    selectedRoomType: IRoomType[];
    hasUserRooms: boolean;
    onlyUserRooms: boolean;

    handleChangeSelectedFilters: (
        occupancyStatus: OccupancyStatusEnum[],
        governanceStatus: GovernanceStatusEnum[],
        roomStatus: RoomStatusEnum[],
        block: IBlock[],
        floor: IFloor[],
        roomType: IRoomType[],
        onlyUserRooms: boolean
    ) => void;

    rooms: IRoom[];
    filteredRooms: IRoom[];
    handleRefreshRooms: (callback?: (rooms: IRoom[]) => void) => void;

    expectedOccupancy: IExpectedOccupancy[];
    handleRefreshExpectedOccupancy: () => void;

    expectedCheckInWithoutRoom: IReservation[];
    handleRefreshExpectedCheckInWithoutRoom: () => void;
}

interface IStateValues {
    selectedOccupancyStatus: OccupancyStatusEnum[];
    selectedGovernanceStatus: GovernanceStatusEnum[];
    selectedRoomStatus: RoomStatusEnum[];
    selectedBlock: IBlock[];
    selectedFloor: IFloor[];
    selectedRoomType: IRoomType[];
    onlyUserRooms: boolean;
    rooms: IRoom[];
    expectedOccupancy: IExpectedOccupancy[];
    expectedCheckInWithoutRoom: IReservation[];
}


const PartnerPmsContext = createContext<IPartnerPmsContext | undefined>(undefined);

const PartnerPmsContextProvider = (props: React.PropsWithChildren) => {
    const { enterprise } = useAuthContext();
    
    const state = useState<IStateValues>({
        selectedOccupancyStatus: [],
        selectedGovernanceStatus: [],
        selectedRoomStatus: [],
        selectedBlock: [],
        selectedFloor: [],
        selectedRoomType: [],
        onlyUserRooms: true,
        rooms: [],
        expectedOccupancy: [],
        expectedCheckInWithoutRoom: [],
    });


    useEffect(() => {
        state.reset();
        // eslint-disable-next-line
    }, [enterprise]);


    const handleChangeSelectedFilters = useCallback((
        occupancyStatus: OccupancyStatusEnum[],
        governanceStatus: GovernanceStatusEnum[],
        roomStatus: RoomStatusEnum[],
        block: IBlock[],
        floor: IFloor[],
        roomType: IRoomType[],
        onlyUserRooms: boolean
    ) => {
        state.update('selectedOccupancyStatus', occupancyStatus);
        state.update('selectedGovernanceStatus', governanceStatus);
        state.update('selectedRoomStatus', roomStatus);
        state.update('selectedBlock', block);
        state.update('selectedFloor', floor);
        state.update('selectedRoomType', roomType);

        state.update('onlyUserRooms', onlyUserRooms);
        // eslint-disable-next-line
    }, [enterprise]);


    const handleRefreshRooms = useCallback((callback = (rooms: IRoom[]) => {}) => {
        const formData = FormHelper.Create(undefined);

        PartnerPmsService.ListRoomOverview(formData).then(resp => {
            const rooms = resp.data.rooms as IRoom[];
            state.update('rooms', rooms);
            callback(rooms);
        }).catch(() => {
            state.update('rooms', []);
            callback([]);
        });
        // eslint-disable-next-line
    }, []);


    const handleRefreshExpectedCheckInWithoutRoom = useCallback(() => {
        const formData = FormHelper.Create(undefined);

        PartnerPmsService.ListExpectedCheckInWithoutRoom(formData).then(resp => {
            state.update('expectedCheckInWithoutRoom', resp.data.reservations as IReservation[]);
        }).catch(() => {
            state.update('expectedCheckInWithoutRoom', []);
        });
        // eslint-disable-next-line
    }, []);


    const handleRefreshExpectedOccupancy = useCallback(() => {
        const formData = FormHelper.Create(undefined);
        
        PartnerPmsService.GetExpectedOccupancy(formData).then(resp => {
            state.update('expectedOccupancy', resp.data.expectedOccupancy as IExpectedOccupancy[]);
        }).catch(() => {
            state.update('expectedOccupancy', []);
        });
        // eslint-disable-next-line
    }, []);

    
    const data: IPartnerPmsContext = {
        selectedOccupancyStatus: state.values.selectedOccupancyStatus,
        selectedGovernanceStatus: state.values.selectedGovernanceStatus,
        selectedRoomStatus: state.values.selectedRoomStatus,
        selectedBlock: state.values.selectedBlock,
        selectedFloor: state.values.selectedFloor,
        selectedRoomType: state.values.selectedRoomType,
        hasUserRooms: (enterprise?.partnerPms.userRooms ?? []).length > 0,
        onlyUserRooms: state.values.onlyUserRooms,
        handleChangeSelectedFilters,
        
        rooms: state.values.rooms,
        filteredRooms: useMemo(() => {
            let data = state.values.rooms;

            const hasUserRooms = (enterprise?.partnerPms.userRooms ?? []).length > 0;
            if (hasUserRooms && state.values.onlyUserRooms) {
                const userRoomsIds = enterprise?.partnerPms.userRooms.map(r => r.id) ?? [];
                data = data.filter(r => userRoomsIds.includes(r.id));
            }
    
            if (state.values.selectedOccupancyStatus.length > 0) {
                const occupancyStatus = Object.values(OccupancyStatusData).filter(s => state.values.selectedOccupancyStatus.includes(s.key)).map(s => s.partnerId ?? '');
                data = data.filter(r => occupancyStatus.includes(r.occupancyStatusId));
            }
    
            if (state.values.selectedGovernanceStatus.length > 0) {
                const governanceStatus = Object.values(GovernanceStatusData).filter(s => state.values.selectedGovernanceStatus.includes(s.key)).map(s => s.partnerId ?? '');
                data = data.filter(r => governanceStatus.includes(r.governanceStatusId));
            }
    
            if (state.values.selectedRoomStatus.length > 0) {
                const roomStatus = Object.values(RoomStatusData).filter(s => state.values.selectedRoomStatus.includes(s.key)).map(s => s.partnerId ?? '');
                data = data.filter(r => {
                    return roomStatus.some(s => {
                        const roomKey = s as keyof(IRoom);
                        const reservationKey = s as keyof(IReservation);
                        return r[roomKey] === true || (r.checkInReservation && r.checkInReservation[reservationKey] === true);
                    });
                });
            }
    
            if (state.values.selectedBlock.length > 0) {
                const block = state.values.selectedBlock.map(b => b.id);
                data = data.filter(r => block.includes(r.blockId));
            }
    
            if (state.values.selectedFloor.length > 0) {
                const floor = state.values.selectedFloor.map(f => f.id);
                data = data.filter(r => floor.includes(r.floorId));
            }
    
            if (state.values.selectedRoomType.length > 0) {
                const roomType = state.values.selectedRoomType.map(r => r.id);
                data = data.filter(r => roomType.includes(r.roomTypeId));
            }

            return data;
            // eslint-disable-next-line
        }, [enterprise, state.values]),
        handleRefreshRooms,

        expectedOccupancy: state.values.expectedOccupancy,
        handleRefreshExpectedOccupancy,
        
        expectedCheckInWithoutRoom: state.values.expectedCheckInWithoutRoom,
        handleRefreshExpectedCheckInWithoutRoom
    }

    return (
        <PartnerPmsContext.Provider value={data}>
            {props.children}
        </PartnerPmsContext.Provider>
    );
}


const usePartnerPmsContext = () => {
    const context = useContext(PartnerPmsContext);

    if (context === undefined) {
        throw new Error('usePartnerPmsContext must be used within a PartnerPmsContextProvider');
    }

    return context;
}

export {
    usePartnerPmsContext
}

export default PartnerPmsContextProvider;